import React, { useState } from 'react';
import { Meetup } from '../types/community';
import { communityService } from '../services/communityService';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  Calendar,
  Clock,
  Users,
  MapPin,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/PetParksContext';

interface MeetupCardProps {
  meetup: Meetup;
  onUpdate?: () => void;
}

export const MeetupCard: React.FC<MeetupCardProps> = ({ meetup, onUpdate }) => {
  const { t, language } = useApp();
  const [rsvpStatus, setRsvpStatus] = useState<
    'attending' | 'maybe' | 'declined' | null
  >(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleRSVP = async (status: 'attending' | 'maybe' | 'declined') => {
    if (
      meetup.current_participants >= meetup.max_participants &&
      status === 'attending'
    ) {
      toast.error(t('meetupFull'));
      return;
    }

    setIsUpdating(true);
    try {
      await communityService.rsvpMeetup(meetup.id, 'current-user-id', status);
      setRsvpStatus(status);
      toast.success(
        status === 'attending'
          ? t('rsvpGoing')
          : status === 'maybe'
            ? t('rsvpMaybe')
            : t('rsvpCancelled')
      );
      onUpdate?.();
    } catch {
      toast.error(t('failedToUpdateRSVP'));
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const date = new Date();
    date.setHours(hour, parseInt(minutes), 0, 0);
    return date.toLocaleTimeString(language, {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const isFull = meetup.current_participants >= meetup.max_participants;
  const spotsLeft = meetup.max_participants - meetup.current_participants;

  return (
    <Card className="overflow-hidden">
      <div className="p-4">
        {/* Header */}
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="mb-1 font-semibold leading-tight">{meetup.title}</h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" />
              <span className="truncate">{meetup.park_name}</span>
            </div>
          </div>
          <Badge
            variant={meetup.status === 'upcoming' ? 'default' : 'secondary'}
            className="capitalize"
          >
            {meetup.status}
          </Badge>
        </div>

        {/* Description */}
        {meetup.description && (
          <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
            {meetup.description}
          </p>
        )}

        {/* Date and Time */}
        <div className="mb-3 grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>{formatDate(meetup.date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>
              {formatTime(meetup.time)} ({meetup.duration_minutes}
              {t('minutesShort')})
            </span>
          </div>
        </div>

        {/* Participants */}
        <div className="mb-3">
          <div className="mb-2 flex items-center justify-between text-sm">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Users className="h-4 w-4" />
              <span>
                {meetup.current_participants} / {meetup.max_participants}{' '}
                {t('going')}
              </span>
            </div>
            {spotsLeft > 0 && spotsLeft <= 3 && (
              <Badge variant="outline" className="text-xs text-orange-600">
                {spotsLeft}{' '}
                {spotsLeft > 1 ? t('spotsLeftPlural') : t('spotLeftSingular')}
              </Badge>
            )}
            {isFull && (
              <Badge variant="secondary" className="text-xs">
                {t('full')}
              </Badge>
            )}
          </div>

          {/* Participant Avatars */}
          {meetup.participants.length > 0 && (
            <div className="flex -space-x-2">
              {meetup.participants.slice(0, 5).map((participant) => (
                <Avatar
                  key={participant.user_id}
                  className="h-8 w-8 border-2 border-background"
                >
                  <AvatarFallback className="text-xs">
                    {participant.username.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              ))}
              {meetup.participants.length > 5 && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-background bg-muted text-xs font-medium">
                  +{meetup.participants.length - 5}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Organizer */}
        <div className="mb-3 flex items-center gap-2 rounded-md bg-muted/50 p-2 text-sm">
          <Avatar className="h-6 w-6">
            <AvatarFallback className="text-xs">
              {meetup.organizer_name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <span className="text-muted-foreground">{t('organizedBy')} </span>
            <span className="font-medium">{meetup.organizer_name}</span>
          </div>
        </div>

        {/* Actions */}
        {meetup.status === 'upcoming' && (
          <div className="flex gap-2">
            {rsvpStatus === 'attending' ? (
              <Button
                variant="outline"
                size="sm"
                className="flex-1 cursor-pointer"
                onClick={() => handleRSVP('declined')}
                disabled={isUpdating}
              >
                <XCircle className="mr-2 h-4 w-4" />
                {t('cancelRSVP')}
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  className="flex-1 cursor-pointer"
                  onClick={() => handleRSVP('attending')}
                  disabled={isUpdating || isFull}
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  {isFull ? t('full') : t('imGoing')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRSVP('maybe')}
                  disabled={isUpdating}
                >
                  {t('maybe')}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
